'use client';

import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib';
import { Minus, Plus } from 'lucide-react';
import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

type CounterProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
};

export function Counter({ value = 1, onChange, min = 1, max = 100, disabled }: CounterProps) {
  // Local state for input field to allow partial/invalid input
  const [inputValue, setInputValue] = useState(String(value));

  // Sync local input when value prop changes (e.g. via + or -)
  useEffect(() => {
    if (String(value) !== inputValue) {
      setInputValue(String(value));
    }
  }, [value, inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Allow empty string for controlled input
    setInputValue(val);

    // Only update parent if it's a valid number
    const num = Number(val);
    if (!Number.isNaN(num) && val !== '') {
      onChange(Math.max(min!, Math.min(max!, num)));
    }
  };

  const handleBlur = () => {
    // Clamp value on blur
    let num = Number(inputValue);
    if (Number.isNaN(num) || inputValue === '') num = min!;
    num = Math.max(min!, Math.min(max!, num));
    setInputValue(String(num));
    onChange(num);
  };

  const handleDecrement = useCallback(() => {
    onChange(Math.max(min, value - 1));
  }, [onChange, min, value]);

  const handleIncrement = useCallback(() => {
    onChange(Math.min(max, value + 1));
  }, [onChange, max, value]);

  return (
    <div className="border-grey-400 hover:border-primary aria-invalid:border-destructive dark:bg-grey-900 dark:border-primary dark:hover:bg-grey-800 hover:bg-grey-50 flex h-12 w-27 max-w-27 items-center justify-between rounded-2xl border bg-neutral-100 px-3 py-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleDecrement}
        disabled={disabled || value === min}
        className="p-0"
        aria-label="Decrease value"
      >
        <Minus
          size={16}
          className={cn(
            (value === min || disabled) && 'opacity-30',
            'text-primary dark:text-neutral-100'
          )}
        />
      </Button>
      <input
        type="number"
        min={min}
        max={max}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        disabled={disabled}
        className="text-grey-900 w-10 appearance-none border-none bg-transparent text-center text-base outline-none dark:text-neutral-100 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        style={{ MozAppearance: 'textfield' }}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleIncrement}
        disabled={disabled || value === max}
        className="p-0"
        aria-label="Increase value"
      >
        <Plus
          size={16}
          className={cn(
            (value === max || disabled) && 'opacity-30',
            'text-primary dark:text-neutral-100'
          )}
        />
      </Button>
    </div>
  );
}
